import { Separator } from "@/components/ui/separator";
import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { useResponseById } from "@/hooks/response-by-id.hook";
import { useUpdateQuizAnswerInBatchMutation } from "@/hooks/update-quiz-answer-in-batch-mutation.hook";
import { validateSelection } from "@/lib/quizzes";
import { cn } from "@/lib/utils";
import { mapChoiceToRequests } from "@/mappers/questionnaires";
import { GetResponsesByQuizIdItem } from "@/requests/questionnaries/responses-by-quiz-id.request";
import { useQuestion } from "@/store/question.context";
import { AnswerMode, Choice, ChoiceType } from "@/types/questionnaries";
import equal from "fast-deep-equal/es6/react";
import { FC, HTMLAttributes, useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import AdditionalComments from "./additional-comments";
import ChoiceList from "./choice-list";
import ClearAnswersButton from "./clear-answers-button";
import EvaluationCriteriaPopover from "./evaluation-criteria-popover";
import EvaluationResults from "./evaluation-results/evaluation-results";
import EvidenceList from "./evidences/evidence-list";
import QuestionCardHeader from "./question-card-header";
import QuestionStatusDescription from "./question-status-description";
import ReuseAnswersButton from "./reuse-answers-button";
import RootChoiceList from "./root-choice-list";
import SwornDeclarationAlert from "./sworn-declaration-alert/sworn-declaration-alert";

type QuestionCardProps = HTMLAttributes<HTMLDivElement> & {
  question: GetResponsesByQuizIdItem;
  topicOrder: number;
  questionOrder: number;
  disabled?: boolean;
  showResults?: boolean;
  showEvidences?: boolean;
};

const QuestionCard: FC<QuestionCardProps> = (props) => {
  // The id of the selected choice at root level. Applies only for single choices
  const [selectedRootChoiceId, setSelectedRootChoiceId] = useState<
    string | null
  >(null);

  const {
    responseId,
    answer,
    quizId,
    choices,
    isAutosaveEnabled,
    setIsSaving,
    setLastSavedTime,
    setIsValidSelection,
    setIsDirty,
  } = useQuestion();

  const prevChoices = useRef<Array<Choice>>([]);

  const choicesInitialized = choices.length > 0;

  const { data: userProfile } = useCurrentUserProfile();

  const selectedRootChoice = choices.find(
    (choice) => choice.id === selectedRootChoiceId,
  );

  const updateAnswersInBatchMutation = useUpdateQuizAnswerInBatchMutation();

  const { data: question } = useResponseById({
    companyId: userProfile?.companyId || "",
    quizId,
    responseId: responseId,
  });

  const saveDebounced = useDebouncedCallback((values: Choice[]) => {
    if (!question) {
      return;
    }

    const rootChoice = { ...fakeRootChoice, choices: values };

    const requests = mapChoiceToRequests(rootChoice);

    setIsSaving(true);

    updateAnswersInBatchMutation.mutate(
      {
        companyId: userProfile?.companyId || "",
        quizId: quizId,
        responseId: question.responseId,
        answers: requests,
        dimension: question.dimension,
        topic: question.topic,
        invalidateQuestionResponses: false,
      },
      {
        onSuccess: () => {
          setLastSavedTime(new Date());
        },
        onSettled: () => {
          setIsSaving(false);
        },
      },
    );
  }, 1500);

  const fakeRootChoice: Choice = {
    id: props.question.id,
    statement: "",
    answerMode: props.question.answerMode,
    choices: choices,
    label: props.question.statement,
    selected: true,
    type: ChoiceType.Composite,
  };

  // Validate and save the choices when they change
  useEffect(() => {
    // If autosave is disabled, don't do anything
    if (props.disabled || !isAutosaveEnabled) {
      return;
    }

    // If the question has no choices, it means that the question is not
    // initialized yet, so we don't need to save the choices
    if (!choicesInitialized) {
      return;
    }

    // If the choices are the same, don't do anything
    if (equal(prevChoices.current, choices)) {
      return;
    }

    const isValid = validateSelection(fakeRootChoice);

    setIsValidSelection(isValid);
    setIsSaving(true);
    saveDebounced(choices);
    setIsDirty(true);

    prevChoices.current = choices;
  }, [choices]);

  /**
   * Initializes the selected choice ID to the first choice marked as selected
   * when the question is loaded with single or radio answer modes.
   */
  useEffect(() => {
    if (!answer) {
      return;
    }

    if (answer.answerMode === AnswerMode.Single) {
      const singleChoices = answer.choices;
      setSelectedRootChoiceId(
        singleChoices.find((c) => c.selected)?.id || null,
      );
    } else {
      setSelectedRootChoiceId(null);
    }
  }, [answer]);

  return (
    <article
      className={cn(
        "flex flex-col gap-6 rounded-xl border p-4",
        props.className,
      )}
    >
      <QuestionCardHeader
        topicOrder={props.topicOrder}
        questionOrder={props.questionOrder}
      />

      <section>
        <h5 className="text-sm font-bold text-[#293038]">
          {props.question.statement}
        </h5>

        {!props.disabled && <ReuseAnswersButton />}

        {props.question.evaluationCriteria.length > 0 && (
          <EvaluationCriteriaPopover
            evaluationCriteria={props.question.evaluationCriteria}
          />
        )}

        <RootChoiceList
          onValueChange={(value) => setSelectedRootChoiceId(value || null)}
          disabled={props.disabled || !isAutosaveEnabled}
        />

        {!props.disabled && selectedRootChoice?.label === "No aplica" && (
          <SwornDeclarationAlert />
        )}
      </section>

      {selectedRootChoice?.type === ChoiceType.Composite &&
        selectedRootChoice.choices.length > 0 && (
          <>
            <Separator />

            <section>
              {selectedRootChoice.statement && (
                <h5 className="text-sm font-bold text-[#293038]">
                  {selectedRootChoice.statement}
                </h5>
              )}

              <ChoiceList
                choice={selectedRootChoice}
                className="my-5"
                level={0}
                disabled={props.disabled || !isAutosaveEnabled}
              />
            </section>
          </>
        )}

      {/* <pre className="text-xs">
        <code>{JSON.stringify(choices, null, 2)}</code>
      </pre> */}

      <AdditionalComments disabled={props.disabled || !isAutosaveEnabled} />

      {!props.disabled && (
        <>
          <Separator />

          <div className="flex w-full flex-col items-start gap-2 md:flex-row md:items-center">
            <QuestionStatusDescription />

            <ClearAnswersButton />
          </div>
        </>
      )}

      {props.showEvidences && <EvidenceList disabled={props.disabled} />}

      {props.showResults && <EvaluationResults />}
    </article>
  );
};

export default QuestionCard;
